import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP);

/**
 * Returns a ref to attach to an element. If `isFresh` is true, the element
 * fades + slides in with a brief accent glow on mount. Otherwise it renders
 * statically. Cleanup on unmount handled by useGSAP.
 *
 * Used for new incidents arriving via Firestore — visual cue that an entry
 * is *new* rather than just "always was there".
 */
export function useFreshHighlight<T extends HTMLElement = HTMLDivElement>(
  isFresh: boolean,
): React.MutableRefObject<T | null> {
  const ref = useRef<T | null>(null);

  useGSAP(
    () => {
      if (!isFresh || !ref.current) return;

      const tl = gsap.timeline();
      // Slide + fade in
      tl.fromTo(
        ref.current,
        {
          opacity: 0,
          x: -10,
          filter: "blur(6px)",
        },
        {
          opacity: 1,
          x: 0,
          filter: "blur(0px)",
          duration: 0.28,
          ease: "power2.out",
        },
      );
      // Accent glow that fades — draws the eye, then settles
      tl.fromTo(
        ref.current,
        {
          boxShadow: "0 0 0 1px rgba(255,107,31,0.5), 0 0 24px 0 rgba(255,107,31,0.3)",
        },
        {
          boxShadow: "0 0 0 0 rgba(255,107,31,0), 0 0 0 0 rgba(255,107,31,0)",
          duration: 1.2,
          ease: "power2.out",
        },
        "-=0.1",
      );
    },
    { scope: ref, dependencies: [isFresh] },
  );

  return ref;
}

/**
 * Returns a ref to a counter element. When `value` changes, briefly tweens
 * the displayed number from old → new (rounding) and pulses scale.
 *
 * Use case: KPI tiles where a counter goes from 2 → 3 due to a new incident.
 */
export function useCounterPulse<T extends HTMLElement = HTMLSpanElement>(
  value: number | string,
): React.MutableRefObject<T | null> {
  const ref = useRef<T | null>(null);
  const prevRef = useRef<number | string>(value);

  useGSAP(
    () => {
      if (!ref.current) return;
      if (prevRef.current === value) return;

      const node = ref.current;
      const prev = prevRef.current;
      prevRef.current = value;

      // If both are numeric, tween the number
      const prevNum = typeof prev === "number" ? prev : Number(prev);
      const nextNum = typeof value === "number" ? value : Number(value);
      const numericTween =
        Number.isFinite(prevNum) && Number.isFinite(nextNum) && prevNum !== nextNum;

      if (numericTween) {
        const obj = { v: prevNum };
        gsap.to(obj, {
          v: nextNum,
          duration: 0.35,
          ease: "power2.out",
          onUpdate: () => {
            node.textContent = String(Math.round(obj.v));
          },
        });
      }

      // Pulse scale + glow
      gsap
        .timeline()
        .fromTo(
          node,
          { scale: 1 },
          { scale: 1.12, duration: 0.12, ease: "power2.out" },
        )
        .to(node, { scale: 1, duration: 0.32, ease: "power3.out" });
    },
    { dependencies: [value] },
  );

  return ref;
}
